import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useGql, useShareableUrl } from '@ah/design';
import { Header, SaveButton } from '@ah/design/mobile';
import {
  SONG_DETAIL_QUERY,
  RECORD_SONG_SAVE_MUTATION,
  RECORD_SONG_UNSAVE_MUTATION,
} from '../shared/queries.ts';
import type { MusicSong } from '../shared/types.ts';
import { SongPlayer } from './SongPlayer.tsx';

export function SongDetail() {
  const { id } = useParams<{ id: string }>();
  const gql = useGql();
  const qc = useQueryClient();
  useShareableUrl(id ? `/music/${id}` : undefined);

  const { data, isLoading, error } = useQuery({
    queryKey: ['music', 'song', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await gql.request<{ song: MusicSong | null }>(SONG_DETAIL_QUERY, { id });
      return res.song;
    },
    enabled: !!id,
    refetchInterval: (q) => {
      const s = q.state.data as MusicSong | null | undefined;
      return s && s.status !== 'ready' && s.status !== 'failed' ? 15_000 : false;
    },
  });

  const trailing = data && id ? (
    <SaveButton
      isSaved={!!data.isSaved}
      onSave={async () => {
        await gql.request(RECORD_SONG_SAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['music', 'saved'] });
        qc.setQueryData(['music', 'song', id], (prev: MusicSong | null | undefined) =>
          prev ? { ...prev, isSaved: true } : prev,
        );
      }}
      onUnsave={async () => {
        await gql.request(RECORD_SONG_UNSAVE_MUTATION, { id });
        qc.invalidateQueries({ queryKey: ['music', 'saved'] });
        qc.setQueryData(['music', 'song', id], (prev: MusicSong | null | undefined) =>
          prev ? { ...prev, isSaved: false } : prev,
        );
      }}
    />
  ) : null;

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Music" backTo="/music" trailing={trailing} />
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error || !data ? (
          <p className="text-sm text-destructive">Could not load this track.</p>
        ) : (
          <>
            <SongPlayer song={data} expandedLyrics />
            {data.artist && (
              <Link
                to={`/music/artists/${data.artist.slug}`}
                className="block bg-card rounded-xl border border-border p-3 active:bg-accent"
              >
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                  Artist
                </p>
                <p className="text-sm font-semibold">{data.artist.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {data.artist.tagline}
                </p>
                {data.artist.bio && (
                  <p className="text-xs text-foreground/80 mt-2 leading-relaxed">
                    {data.artist.bio}
                  </p>
                )}
              </Link>
            )}
          </>
        )}
      </main>
    </div>
  );
}
